import {gql} from '@apollo/client';
import {css, StyleSheet} from 'aphrodite';
import {DocumentNode} from 'graphql';
import React, {useEffect} from 'react';

import * as OrganizationActions from 'actions/OrganizationActions';
import * as analytics from 'analytics/Analytics';
import {setWindowTitle} from 'BenchlingUtil';
import {IDENTITY_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import navigateToURL from 'lib/navigateToURL';
import UserDataAccessTab from 'orgs/UserDataAccessTab';
import TenantBreadcrumbs from 'tenants/TenantBreadcrumbs';
import Avatar from 'ui-library/avatar/Avatar';
import {AvatarShape, AvatarSize} from 'ui-library/avatar/AvatarConstants';
import {Header1} from 'ui-library/Headers';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import * as nyc from 'ui-library/NYC';
import SectionTabs from 'ui-library/SectionTabs';

import {UserPageQuery, UserPageQueryVariables} from './__generated/UserPageQuery';
import UserBasicSettingsTab from './UserBasicSettingsTab';

type UserPageQueryUser = ExtractByTypename<UserPageQuery['user'], 'UserNode'>;

export function navigateToUserPage(userId: string): void {
  analytics.trackEvent(IDENTITY_ANALYTICS_ACTIONS.CLICKED_PARTY_SETTINGS, {
    objectType: 'user',
    objectId: userId,
  });
  navigateToURL(`users/${userId}`);
}

export enum UserSettingTab {
  SETTINGS_TAB = 'SETTINGS_TAB',
  ACCESS_TAB = 'ACCESS_TAB',
}

const TAB_TO_DISPLAY: {[key in UserSettingTab]: string} = {
  [UserSettingTab.SETTINGS_TAB]: 'Basic Settings',
  [UserSettingTab.ACCESS_TAB]: 'Access',
};

export const USER_PAGE_QUERY: DocumentNode = gql`
  query UserPageQuery($userId: StrictString!) @skipAudit @engComponent(name: IDENTITY) {
    user(id: $userId) {
      ... on UserNode {
        id
        name
        handle
        avatarUrl
        email
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

interface UserPageProps {
  user: UserPageQueryUser;
  activeTab: UserSettingTab;
}

export interface QueryingUserPageProps {
  userId: string;
  activeTab: string;
}

function UserPage(props: UserPageProps): JSX.Element {
  useEffect(() => {
    setWindowTitle(props.user.name);
  }, [props.user.name]);
  return (
    <nyc.Container size="lg" className={css(styles.container)}>
      <nyc.Row className="teamPage-container">
        <nyc.Col lg={1} />
        <nyc.Col lg={21}>
          <TenantBreadcrumbs />
          <div className="u-vertical-center-items u-spacing-bottom">
            <Avatar size={AvatarSize.XLARGE} shape={AvatarShape.CIRCLE} entity={props.user} />
            <div className="u-small-spacing-left">
              <Header1>{props.user.name}</Header1>
              <span className="u-gray-darker">User</span>
            </div>
          </div>
          <SectionTabs
            tabs={Object.keys(TAB_TO_DISPLAY).map((userSettingTab: UserSettingTab) => ({
              handleClick: () => {
                OrganizationActions.updateActiveTab(props.user.id, userSettingTab);
              },
              ariaControls: `${userSettingTab}_PANEL`,
              id: userSettingTab,
              text: TAB_TO_DISPLAY[userSettingTab],
              isActive: props.activeTab === userSettingTab,
            }))}
          />
        </nyc.Col>
      </nyc.Row>
      <nyc.Row
        aria-labelledby={props.activeTab}
        className="organizationPage-sectionContainer"
        id={`${props.activeTab}_PANEL`}
        role="tabpanel"
      >
        <nyc.Col lg={22}>
          {(() => {
            switch (props.activeTab) {
              case UserSettingTab.SETTINGS_TAB:
                return <UserBasicSettingsTab user={props.user} />;
              case UserSettingTab.ACCESS_TAB:
                return <UserDataAccessTab userId={props.user.id} />;
              default:
                return null;
            }
          })()}
        </nyc.Col>
      </nyc.Row>
    </nyc.Container>
  );
}

export default function QueryingUserPage(props: QueryingUserPageProps): JSX.Element {
  return (
    <BenchlingQuery<UserPageQuery, UserPageQueryVariables>
      query={USER_PAGE_QUERY}
      renderLoading={() => <CenteredHorizontalThrobber />}
      variables={{userId: props.userId}}
    >
      {({user}) => {
        if (isInaccessible(user)) {
          return (
            <div>
              <div className="u-gray-darker">Inaccessible user id</div>
            </div>
          );
        }
        return (
          <UserPage
            user={user}
            activeTab={
              UserSettingTab[props.activeTab as keyof typeof UserSettingTab] ?? UserSettingTab.SETTINGS_TAB
            }
          />
        );
      }}
    </BenchlingQuery>
  );
}

const styles = StyleSheet.create({
  container: {
    height: '100%',
  },
});
